import React, { useEffect, useMemo, useState } from 'react';
import { Button } from '@/components/Button';
import SideBarModal from '@/components/Modal/SideBarModal';
import {
  createSandboxOrder,
  SANDBOX_ORDER_STOCK_PER_SKU,
  SandboxExchange,
} from '@/services/api/sandbox';
import { extractApiErrorMessage } from '@/utils/errorHandler';

const rand = (n: number) =>
  Array.from({ length: n }, () =>
    'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789'.charAt(
      Math.floor(Math.random() * 36)
    )
  ).join('');

const newExternalId = () => `SBX-ORD-${rand(8)}`;

/** Tomorrow in Jakarta time (`YYYY-MM-DD`) — the API refuses past dates. */
const tomorrowWib = () =>
  new Date(Date.now() + 7 * 3_600_000 + 86_400_000).toISOString().slice(0, 10);

const MAX_LINES = 5;

/** The sample recipient and destination every sandbox order is sent with. */
const SAMPLE_RECIPIENT = { name: 'Apotek Fatih Farma', phone: '081222222222' };
const SAMPLE_DESTINATION = {
  address:
    'Jl. Tebet Raya No. 12, Tebet Tim., Kec. Tebet, Kota Jakarta Selatan, Daerah Khusus Ibukota Jakarta 12820',
  latitude: -6.229671,
  longitude: 106.854134,
  notes: '',
};

type Line = { sku: string; qty: string };

/**
 * Creates one sandbox outbound order — the same call a partner makes with
 * their dash_test_ key. SKUs come from the client's catalog at Dash (an
 * unknown one is refused, as in production); every SKU holds
 * `SANDBOX_ORDER_STOCK_PER_SKU` units here, so ordering more shows the
 * out-of-stock answer. Reusing an external ID answers EXISTS and opens that
 * order instead.
 */
const NewSandboxOrderModal: React.FC<{
  open: boolean;
  onClose: () => void;
  /** Called with the order it created (or found) and the API exchange. */
  onCreated: (orderId: string, exchange: SandboxExchange) => void;
}> = ({ open, onClose, onCreated }) => {
  const [externalId, setExternalId] = useState(newExternalId);
  const [deliveryDate, setDeliveryDate] = useState(tomorrowWib);
  const [lines, setLines] = useState<Line[]>([{ sku: '', qty: '1' }]);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (open) {
      setExternalId(newExternalId());
      setDeliveryDate(tomorrowWib());
      setError(null);
      setSubmitting(false);
    }
  }, [open]);

  const body = useMemo(
    () => ({
      externalID: externalId.trim(),
      expectedDeliveryDate: deliveryDate,
      recipient: SAMPLE_RECIPIENT,
      destination: SAMPLE_DESTINATION,
      lines: lines.map((l) => ({ sku: l.sku.trim(), qty: Number(l.qty) })),
    }),
    [externalId, deliveryDate, lines]
  );

  const filled = lines.every((l) => l.sku.trim() && Number(l.qty) > 0);
  const canSubmit = !submitting && !!externalId.trim() && filled;

  const setLine = (i: number, patch: Partial<Line>) =>
    setLines((ls) => ls.map((l, j) => (j === i ? { ...l, ...patch } : l)));

  const submit = async () => {
    if (!canSubmit) return;
    setSubmitting(true);
    setError(null);
    try {
      const exchange = await createSandboxOrder(body);
      const order = (
        exchange.response as { data?: { order?: { id?: string } } }
      )?.data?.order;
      if (exchange.status < 400 && order?.id) {
        onCreated(order.id, exchange);
        return;
      }
      setError(
        (exchange.response as { error?: string })?.error ??
          `HTTP ${exchange.status}`
      );
    } catch (e) {
      setError(extractApiErrorMessage(e, 'Could not reach the API'));
    } finally {
      setSubmitting(false);
    }
  };

  // No width here: each field sets its own (`w-full` would beat `w-24`).
  const inputClass =
    'rounded-lg border border-stroke-sub-300 bg-bg-white-0 px-3 py-2 text-sm text-text-strong-950 focus:border-primary focus:outline-none';

  return (
    <SideBarModal
      isOpen={open}
      onClose={onClose}
      width="md"
      className="max-w-[calc(100vw-2rem)]"
      header={
        <div>
          <h2 className="text-lg font-semibold text-text-strong-950">
            New sandbox order
          </h2>
          <p className="mt-0.5 text-xs text-text-sub-600">
            POST /v1/integration/outbound-orders, in sandbox mode.
          </p>
        </div>
      }
      footer={
        <div className="flex justify-end gap-2">
          <Button variant="white" onClick={onClose} disabled={submitting}>
            Cancel
          </Button>
          <Button onClick={submit} disabled={!canSubmit}>
            {submitting ? 'Creating…' : 'Create order'}
          </Button>
        </div>
      }
    >
      <div className="space-y-5 p-6">
        <div>
          <div className="mb-1.5 flex items-center justify-between">
            <label
              htmlFor="sbx-order-ext"
              className="text-sm font-medium text-text-strong-950"
            >
              External ID
            </label>
            <button
              type="button"
              onClick={() => setExternalId(newExternalId())}
              className="text-xs font-medium text-primary hover:underline"
            >
              New ID
            </button>
          </div>
          <input
            id="sbx-order-ext"
            type="text"
            value={externalId}
            onChange={(e) => setExternalId(e.target.value)}
            className={`${inputClass} w-full font-mono`}
          />
          <p className="mt-1 text-xs text-text-sub-600">
            Your order id, the idempotency key — reuse one to get EXISTS back.
          </p>
        </div>

        <div>
          <label
            htmlFor="sbx-order-date"
            className="mb-1.5 block text-sm font-medium text-text-strong-950"
          >
            Expected delivery
          </label>
          <input
            id="sbx-order-date"
            type="date"
            value={deliveryDate}
            onChange={(e) => setDeliveryDate(e.target.value)}
            className={`${inputClass} w-44 tabular-nums`}
          />
        </div>

        <fieldset>
          <legend className="mb-1.5 text-sm font-medium text-text-strong-950">
            Lines
          </legend>
          <div className="space-y-2">
            {lines.map((l, i) => (
              // eslint-disable-next-line react/no-array-index-key -- rows are positional
              <div key={i} className="flex items-center gap-2">
                <input
                  aria-label={`SKU, line ${i + 1}`}
                  placeholder="SKU from your catalog"
                  value={l.sku}
                  onChange={(e) => setLine(i, { sku: e.target.value })}
                  className={`${inputClass} min-w-0 flex-1 font-mono`}
                />
                <input
                  aria-label={`Qty, line ${i + 1}`}
                  type="number"
                  min={1}
                  value={l.qty}
                  onChange={(e) => setLine(i, { qty: e.target.value })}
                  className={`${inputClass} w-24 shrink-0 tabular-nums`}
                />
                <button
                  type="button"
                  aria-label={`Remove line ${i + 1}`}
                  disabled={lines.length === 1}
                  onClick={() => setLines((ls) => ls.filter((_, j) => j !== i))}
                  className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg text-text-sub-600 hover:bg-bg-weak-50 disabled:opacity-40"
                >
                  <i className="ri-delete-bin-line" />
                </button>
              </div>
            ))}
          </div>
          {lines.length < MAX_LINES && (
            <button
              type="button"
              onClick={() => setLines((ls) => [...ls, { sku: '', qty: '1' }])}
              className="mt-2 text-xs font-medium text-primary hover:underline"
            >
              <i className="ri-add-line mr-1" />
              Add line
            </button>
          )}
          <p className="mt-2 text-xs text-text-sub-600">
            In Sandbox every SKU of your catalog holds{' '}
            <b className="text-text-strong-950">
              {SANDBOX_ORDER_STOCK_PER_SKU}
            </b>{' '}
            units — order more to see the out-of-stock answer.
          </p>
        </fieldset>

        <div className="rounded-lg bg-bg-weak-50 px-3 py-2.5 text-xs text-text-sub-600">
          <p className="font-medium text-text-strong-950">
            Sent to a sample recipient
          </p>
          <p className="mt-0.5">
            {SAMPLE_RECIPIENT.name} · {SAMPLE_DESTINATION.address}
          </p>
        </div>

        {error && (
          <div
            role="alert"
            className="rounded-lg bg-error-lighter px-3 py-2 text-sm text-error-dark"
          >
            {error.includes('; ') ? (
              <ul className="list-disc space-y-0.5 pl-4">
                {error.split('; ').map((e) => (
                  <li key={e}>{e}</li>
                ))}
              </ul>
            ) : (
              error
            )}
          </div>
        )}
      </div>
    </SideBarModal>
  );
};

export default NewSandboxOrderModal;
